
  import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/FirebaseAuthContext';
import { ArrowLeft, Music, Check, X, Loader2 } from 'lucide-react';
import { checkSlugAvailability, generateUrlSlug } from '@/lib/utils';
import { useMobile } from '@/features/shared/contexts/MobileContext';
import MobileSignupPage from '@/features/shared/components/MobileSignupPage';
import { COUNTRIES } from '@/lib/countries';
import SmartDateOfBirth from '@/components/SmartDateOfBirth';
import AgentArtistInviteBand from '@/components/landing/AgentArtistInviteBand';

  const SignupPage = () => {
  const [searchParams] = useSearchParams();
  const loginWithAgentHref = useMemo(() => {
    const a = searchParams.get('agent')?.trim();
    return a ? `/login?agent=${encodeURIComponent(a.toUpperCase())}` : '/login';
  }, [searchParams]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [artistName, setArtistName] = useState('');
  const [profileUrl, setProfileUrl] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState(null); // Will be {day, month, year, string} object
  const [country, setCountry] = useState('');
  const [acceptedPrivacyPolicy, setAcceptedPrivacyPolicy] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingSlug, setCheckingSlug] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState(null);
  const { signUp } = useAuth();
  const mobile = useMobile();

  // Real-time slug availability checking
    const checkSlugAvailabilityRealTime = async (slug) => {
      if (!slug || slug.length < 3) {
        setSlugAvailable(null);
        return;
      }
      
      setCheckingSlug(true);
      try {
        const isAvailable = await checkSlugAvailability(slug);
        setSlugAvailable(isAvailable);
      } catch (error) {
        console.error('Error checking slug availability:', error);
        setSlugAvailable(false);
      } finally {
        setCheckingSlug(false);
      }
    };

    // Auto-generate profile URL from artist name
    const handleArtistNameChange = (e) => {
      const name = e.target.value;
      setArtistName(name);
      
      // Always update the profile URL as the user types
      if (name) {
        const generatedSlug = generateUrlSlug(name);
        setProfileUrl(generatedSlug);
        checkSlugAvailabilityRealTime(generatedSlug);
      } else {
        // Clear the profile URL if artist name is empty
        setProfileUrl('');
        setSlugAvailable(null);
      }
    };

    // Handle profile URL changes
    const handleProfileUrlChange = (e) => {
      const slug = generateUrlSlug(e.target.value);
      setProfileUrl(slug);
      checkSlugAvailabilityRealTime(slug);
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      
      // Validate profile URL
      if (!profileUrl || profileUrl.length < 3) {
        alert('Please enter a valid profile URL (at least 3 characters)');
        return;
      }
      
      if (slugAvailable === false) {
        alert('This profile URL is already taken. Please choose a different one.');
        return;
      }
      
      // Validate Full Name
      if (!fullName || fullName.trim().length < 2) {
        alert('Please enter your full legal name');
        return;
      }
      
      // Validate DOB
      if (!dateOfBirth || !dateOfBirth.day || !dateOfBirth.month || !dateOfBirth.year) {
        alert('Please select your complete date of birth');
        return;
      }
      
      // Validate age (must be 18+)
      const birthDate = new Date(dateOfBirth.year, dateOfBirth.month - 1, dateOfBirth.day);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      const actualAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) ? age - 1 : age;
      
      if (actualAge < 18) {
        alert('You must be at least 18 years old to create an account');
        return;
      }
      
      // Validate checkboxes
      if (!acceptedPrivacyPolicy) {
        alert('Please accept the Privacy Policy to continue');
        return;
      }
      
      if (!acceptedTerms) {
        alert('Please accept the Terms and Conditions to continue');
        return;
      }

      if (!country || !String(country).trim()) {
        alert('Please select your country of residence');
        return;
      }
      
      setLoading(true);
      
      try {
        // Format DOB for backend (DD/MM/YYYY string for backward compatibility)
        const dobString = dateOfBirth.string || 
          `${String(dateOfBirth.day).padStart(2, '0')}/${String(dateOfBirth.month).padStart(2, '0')}/${dateOfBirth.year}`;
        
        const agentRaw = searchParams.get('agent')?.trim();
        const agentCode = agentRaw ? agentRaw.toUpperCase() : null;

        const result = await signUp(email, password, {
            data: {
                fullName: fullName.trim(),
                artistName: artistName,
                profileUrl: profileUrl,
                dateOfBirth: dobString, // Backward compatible string format
                dateOfBirthObject: dateOfBirth, // Also pass the structured object
                country: country, // Pass 2-letter ISO country code
                agentCode,
            }
        });
        
        if (result.error) {
          setLoading(false);
          alert(result.error.message || 'Signup failed. Please try again.');
          return;
        }
        
        // Signup successful - reset loading state
        // The AuthProvider will handle navigation via onAuthStateChanged
        setLoading(false);
        
        // Fallback: If redirect doesn't happen within 3 seconds, manually redirect
        setTimeout(() => {
          // Check if we're still on the signup page (redirect didn't happen)
          if (window.location.pathname.includes('/signup')) {
            console.log('⚠️ Redirect didn\'t happen, manually navigating to dashboard...');
            window.location.href = '/admin';
          }
        }, 3000);
      } catch (err) {
        console.error('Signup error:', err);
        setLoading(false);
        
        // Show user-friendly error message
        const errorMessage = err.message || 'An error occurred during signup. Please try again.';
        alert(errorMessage);
        
        if (err.code === 'stripe-account-creation-failed' ||
            err.message?.includes('payment account')) {
          alert('Unable to complete payment setup. This is required to receive payments. Please check your information and try again. If the problem persists, contact support.');
        }
      }
    };

    // Return mobile version if on mobile device
    if (mobile.isMobile) {
      return <MobileSignupPage />;
    }

    return (
      <>
        <Helmet>
          <title>Artist Signup - Your Song Request</title>
          <meta name="description" content="Create your artist account and start managing song requests from your audience during live performances." />
          <meta property="og:title" content="Artist Signup - Your Song Request" />
          <meta property="og:description" content="Create your artist account and start managing song requests from your audience during live performances." />
        </Helmet>

        <div className="min-h-screen bg-black flex flex-col items-center justify-start px-4 py-8 sm:justify-center sm:py-10">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/10 via-purple-900/10 to-cyan-900/10"></div>
          
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="relative z-10 w-full max-w-lg"
          >
            <AgentArtistInviteBand mode="signup" />
            <Link to="/" className="inline-flex items-center text-blue-400 hover:text-blue-300 mb-8 transition-colors">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Link>

            <Card className="glass-effect border-gray-800 mx-auto max-w-md w-full">
              <CardHeader className="text-center">
                <div className="flex justify-center mb-4">
                  <Music className="w-12 h-12 text-blue-500" />
                </div>
                <CardTitle className="text-2xl font-bold text-white">Create Artist Account</CardTitle>
                <CardDescription className="text-gray-400">
                  Join the platform and start getting paid to receive song requests
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-4 text-center">
                  <p className="text-gray-400">
                    Already have an account?{' '}
                    <Link to={loginWithAgentHref} className="text-blue-400 hover:text-blue-300 font-semibold">
                      Login here
                    </Link>
                  </p>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="fullName" className="text-white">Full Legal Name <span className="text-red-400">*</span></Label>
                    <Input
                      id="fullName"
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      required
                      className="bg-gray-900 border-gray-700 text-white"
                      placeholder="John Smith"
                    />
                    <p className="text-gray-400 text-xs">
                      Required for identity verification and receiving payouts.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="artistName" className="text-white">Artist Name</Label>
                    <Input
                      id="artistName"
                      type="text"
                      value={artistName}
                      onChange={handleArtistNameChange}
                      required
                      className="bg-gray-900 border-gray-700 text-white"
                      placeholder="Your stage name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="profileUrl" className="text-white">Profile URL</Label>
                    <div className="relative">
                      <Input
                        id="profileUrl"
                        type="text"
                        value={profileUrl}
                        onChange={handleProfileUrlChange}
                        required
                        className={`bg-gray-900 border-gray-700 text-white pr-10 ${
                          slugAvailable === true ? 'border-green-500' : 
                          slugAvailable === false ? 'border-red-500' : ''
                        }`}
                        placeholder="your-artist-name"
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                        {checkingSlug && (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                        )}
                        {!checkingSlug && slugAvailable === true && (
                          <Check className="h-4 w-4 text-green-500" />
                        )}
                        {!checkingSlug && slugAvailable === false && (
                          <X className="h-4 w-4 text-red-500" />
                        )}
                      </div>
                    </div>
                    <p className="text-gray-400 text-xs">
                      This will be your public profile URL: yoursongrequest.com/{profileUrl || 'your-artist-name'}
                    </p>
                    {slugAvailable === false && (
                      <p className="text-red-400 text-xs">This URL is already taken. Please choose a different one.</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-white">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="bg-gray-900 border-gray-700 text-white"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-white">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="bg-gray-900 border-gray-700 text-white"
                      placeholder="••••••••"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="country" className="text-white">Country of Residence <span className="text-red-400">*</span></Label>
                    <select
                      id="country"
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      required
                      className="w-full bg-gray-900 border border-gray-700 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select your country...</option>
                      {COUNTRIES.map((countryOption) => (
                        <option key={countryOption.code} value={countryOption.code}>
                          {countryOption.label}
                        </option>
                      ))}
                    </select>
                    <p className="text-gray-400 text-xs">
                      Please select your country of residence. This determines your currency and payment settings.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="dateOfBirth" className="text-white">
                      Date of Birth <span className="text-red-400">*</span>
                      {country === 'US' && (
                        <span className="text-gray-400 text-xs ml-2">(MM/DD/YYYY format)</span>
                      )}
                      {country !== 'US' && (
                        <span className="text-gray-400 text-xs ml-2">(DD/MM/YYYY format)</span>
                      )}
                    </Label>
                    <SmartDateOfBirth
                      country={country}
                      value={dateOfBirth}
                      onChange={setDateOfBirth}
                      className="w-full"
                    />
                    <p className="text-gray-400 text-xs">
                      You must be at least 18 years old. Required for compliance and receiving payouts.
                    </p>
                  </div>
                  
                  {/* Privacy Policy and Terms */}
                  <div className="space-y-3 pt-2">
                    <div className="flex items-start space-x-2">
                      <input
                        type="checkbox"
                        id="privacyPolicy"
                        checked={acceptedPrivacyPolicy}
                        onChange={(e) => setAcceptedPrivacyPolicy(e.target.checked)}
                        required
                        className="mt-1 w-4 h-4 text-blue-600 bg-gray-900 border-gray-700 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="privacyPolicy" className="text-sm text-gray-300 cursor-pointer">
                        I accept the{' '}
                        <Link to="/privacy-policy" target="_blank" className="text-blue-400 hover:text-blue-300 underline">
                          Privacy Policy
                        </Link>
                      </label>
                    </div>
                    <div className="flex items-start space-x-2">
                      <input
                        type="checkbox"
                        id="terms"
                        checked={acceptedTerms}
                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                        required
                        className="mt-1 w-4 h-4 text-blue-600 bg-gray-900 border-gray-700 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="terms" className="text-sm text-gray-300 cursor-pointer">
                        I accept the{' '}
                        <Link to="/terms-of-service" target="_blank" className="text-blue-400 hover:text-blue-300 underline">
                          Terms and Conditions
                        </Link>
                      </label>
                    </div>
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={loading || !acceptedPrivacyPolicy || !acceptedTerms}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 neon-glow disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating Account...
                      </>
                    ) : (
                      'Create Account'
                    )}
                  </Button>
                </form>
                
                {/* Alternative account type routes */}
                <div className="mt-6 space-y-3">
                  <div className="p-4 bg-purple-900/20 border border-purple-700/50 rounded-lg">
                    <p className="text-purple-200 text-sm mb-2">
                      🎤 Are you a DJ / Karaoke Host?
                    </p>
                    <Link 
                      to="/karaoke/signup" 
                      className="inline-flex items-center text-purple-300 hover:text-purple-200 font-semibold text-sm transition-colors"
                    >
                      Go to Karaoke Sign Up
                    </Link>
                    <p className="text-purple-400 text-xs mt-2 italic">
                      Karaoke Mode is designed specifically for DJs and karaoke hosts.
                    </p>
                  </div>

                  <div className="p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg">
                    <p className="text-blue-200 text-sm mb-2">
                      💼 Are you a booking Agent?
                    </p>
                    <Link 
                      to="/agent/signup" 
                      className="inline-flex items-center text-blue-300 hover:text-blue-200 font-semibold text-sm transition-colors"
                    >
                      Go to Agent Sign Up
                    </Link>
                    <p className="text-blue-400 text-xs mt-2 italic">
                      Agent accounts can invite and manage artist rosters.
                    </p>
                  </div>
                </div>
                
                <div className="mt-6 text-center">
                  <p className="text-gray-400">
                    Already have an account?{' '}
                    <Link to={loginWithAgentHref} className="text-blue-400 hover:text-blue-300 font-semibold">
                      Login here
                    </Link>
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </>
    );
  };

  export default SignupPage;
